import { AutoComplete } from 'primereact/autocomplete'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaMoneyBillWave } from 'react-icons/fa'
import { IoWarningOutline } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { getLowStockMessage, isLowStockItem } from '../utils/StockAlerts'

const SearchPriceListField = ({
  searchedItem,
  setSearchedItem,
  handleSelectSearchedItem,
  handleSearchItems,
  loading = false,
  itemsSuggestions,
  disabled = false,
  height = 'h-30',
  isFull = false,
  isApp = false,
  payerID = '',
  showNeedApproval = true
}) => {
  const { t } = useTranslation()
  const { cashPayers } = useSelector(state => state?.visits)
  const cashPayerId = cashPayers?.[0]?.payer_id

  const isGrouped = (options = []) =>
    Array.isArray(options) && options.length > 0 && options[0].options
  const itemTemplate = item => {
    const isApproval = item?.item?.payer_details?.[0]?.is_approval
    const category =
      item?.item?.payer_details?.[0]?.payer?.category?.toLowerCase() ||
      (item?.fromInsurance || payerID !== cashPayerId ? 'insurance' : 'cash')
    const isRtl = document.documentElement.dir === 'rtl'
    const showLowStockAlert = isLowStockItem(item?.item)
    const itemData = item?.item || {}
    const selectedItemPayer = itemData?.payer_details?.[0] || {}
    const code =
      item?.code ||
      itemData?.non_standard_code ||
      selectedItemPayer?.non_standard_code ||
      itemData?.standard_code ||
      'N/A'
    const itemName = item?.itemName || itemData?.price_list_type?.name_en || ''
    const description =
      item?.description ||
      itemData?.non_standard_description ||
      selectedItemPayer?.non_standard_description ||
      itemData?.standard_description ||
      item?.label

    const getBgColor = () => {
      if (category === 'cash') return 'bg-green-50/50'
      if (category === 'insurance') {
        return isApproval ? 'bg-rose-50/50' : 'bg-blue-50/50'
      }
      return 'bg-white'
    }

    const getBorderColor = () => {
      if (category === 'cash') return 'border-green-100'
      if (category === 'insurance') {
        return isApproval ? 'border-rose-100' : 'border-blue-100'
      }
      return 'border-gray-100'
    }

    return (
      <div
        className={`grid w-full grid-cols-[120px_minmax(360px,1.2fr)_minmax(180px,0.6fr)_140px] items-center gap-4 border-b px-4 py-3.5 transition-all cursor-pointer ${getBorderColor()} ${getBgColor()} ${
          isRtl ? 'text-right' : 'text-left'
        }`}
      >
        <div className='min-w-0'>
          <span className='inline-flex max-w-full items-center rounded-md bg-blue-50 px-3 py-1.5 text-[13px] font-semibold text-blue-600'>
            <span className='truncate'>{code}</span>
          </span>
        </div>

        <div
          className={`flex min-w-0 items-center gap-2 overflow-hidden ${
            isRtl ? 'flex-row-reverse' : ''
          }`}
        >
          <span className='truncate text-[14px] font-bold uppercase tracking-normal text-slate-800'>
            {itemName || t('Item')}
          </span>
          {item?.nameAr && (
            <span
              className='shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-[12px] font-semibold text-primary'
              dir='rtl'
            >
              {item.nameAr}
            </span>
          )}
        </div>

        <div className='min-w-0'>
          <span className='line-clamp-2 text-[13.5px] font-medium leading-5 text-slate-600'>
            {description || item?.label}
          </span>
        </div>

        <div
          className={`flex min-w-0 items-center gap-2 ${
            isRtl ? 'justify-start' : 'justify-end'
          }`}
        >
          {(category === 'cash' ||
            (showNeedApproval && isApproval) ||
            showLowStockAlert) && (
          <>
            {showLowStockAlert && (
              <div className='flex items-center gap-1.5 whitespace-nowrap'>
                <IoWarningOutline size={16} className='text-amber-600' />
                <span className='text-[13.5px] font-semibold text-amber-700 leading-none'>
                  ({t(getLowStockMessage(item?.item))})
                </span>
              </div>
            )}
            {category === 'cash' ? (
              <div className='flex items-center gap-1.5 whitespace-nowrap'>
                <FaMoneyBillWave size={16} className='text-primary' />
                <span className='text-[13.5px] font-semibold text-black leading-none'>
                  ({t('Cash')})
                </span>
              </div>
            ) : showNeedApproval && isApproval ? (
              <div className='flex items-center gap-1.5 whitespace-nowrap'>
                <IoWarningOutline size={16} className='text-green-600' />
                <span className='text-[13.5px] font-semibold text-black leading-none'>
                  ({'Need Approval'})
                </span>
              </div>
            ) : null}
          </>
          )}
        </div>
      </div>
    )
  }

  const appTempelate = item => {
    const isRtl = document.documentElement.dir === 'rtl'
    const showLowStockAlert = isLowStockItem(item?.item)
    return (
      <div
        className={`flex items-center justify-between p-3 gap-4 w-full border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
          isRtl ? 'flex-row-reverse' : ''
        }`}
      >
        <div
          className={`flex flex-grow min-w-0 gap-3 items-center ${
            isRtl ? 'flex-row-reverse' : ''
          }`}
        >
          <span className='font-medium text-[14px] text-gray-800'>
            {item?.label}
          </span>
          {item?.nameAr && (
            <span className='text-[14px] font-medium text-primary' dir='rtl'>
              ({item.nameAr})
            </span>
          )}
        </div>
        {showLowStockAlert && (
          <div className='flex-shrink-0 flex items-center gap-1.5 whitespace-nowrap'>
            <IoWarningOutline size={16} className='text-amber-600' />
            <span className='text-[13.5px] font-semibold text-amber-700 leading-none'>
              ({t(getLowStockMessage(item?.item))})
            </span>
          </div>
        )}
      </div>
    )
  }

  const isRtl = document.documentElement.dir === 'rtl'
  const panelHeaderTemplate = () => (
    <div
      className={`grid grid-cols-[120px_minmax(360px,1.2fr)_minmax(180px,0.6fr)_140px] gap-4 border-b border-gray-200 bg-slate-50 px-4 py-3 text-[12px] font-bold uppercase tracking-normal text-slate-500 ${
        isRtl ? 'text-right' : 'text-left'
      }`}
    >
      <span>{t('Code')}</span>
      <span>{t('Item Name')}</span>
      <span>{t('Description')}</span>
      <span className={isRtl ? 'text-left' : 'text-right'}>{t('Status')}</span>
    </div>
  )

  return (
    <div>
      <AutoComplete
        disabled={disabled}
        loadingIcon={
          loading ? (
            <i
              className={`pi pi-spin pi-spinner text-primary absolute top-[13px] ${
                isRtl ? 'left-[12px]' : 'right-[12px]'
              } `}
            />
          ) : (
            <span />
          )
        }
        value={searchedItem}
        field='label'
        suggestions={itemsSuggestions}
        delay={700}
        completeMethod={handleSearchItems}
        {...(isGrouped(itemsSuggestions) && {
          optionGroupLabel: 'label',
          optionGroupChildren: 'options'
        })}
        onChange={e => {
          setSearchedItem(e?.target?.value)
        }}
        onSelect={handleSelectSearchedItem}
        itemTemplate={isApp ? appTempelate : itemTemplate}
        {...(!isApp && { panelHeaderTemplate })}
        forceSelection
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
        emptyMessage={t('No items found for this search')}
        showEmptyMessage={true}
        placeholder={t('visit_price_items_placeholder')}
        pt={{
          root: { className: 'w-full' },
          input: {
            root: {
              className: `input-field !w-full ${height}`
            }
          },
          panel: {
            className: `!overflow-hidden !shadow-lg rounded-[8px] mt-2 !w-fit !max-w-[calc(100vw-2rem)] bg-white border border-gray-200 ${
              isFull ? '!max-h-[80vh] !bg-white' : '!max-h-[460px]'
            }`
          },
          listwrapper: {
            className: `${isFull ? '!max-h-[70vh]' : '!max-h-[360px]'}`
          },
          list: { className: '!py-0' },
          itemGroup: {
            className:
              '!text-primary uppercase !px-4 !py-2 !bg-gray-50 !text-[11px] !font-bold border-b border-gray-100'
          },
          item: {
            className:
              "!p-0 !text-gray-600 data-[p-highlight='true']:!border-l-[3px] data-[p-highlight='true']:!border-primary rtl:data-[p-highlight='true']:!border-l-0 rtl:data-[p-highlight='true']:!border-r-[3px] rtl:data-[p-highlight='true']:!border-primary"
          },
          loadingIcon: {
            className: `absolute top-[16px] ${
              isRtl ? 'left-[12px]' : 'right-[12px]'
            }`
          },
          emptymesage: { className: '!text-center !p-4 !text-gray-400 !italic' }
        }}
      />
    </div>
  )
}

export default SearchPriceListField
